import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import { getGuideAccent } from "../../utils/guideAccent";

// Template330 (Edimburgo): Grassmarket y Victoria Street
const Template330 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const portadaImage = getImageSrc("portada");
	const torreSticker =
		getCanvasImageUrl("hero-sticker") || getCanvasImageUrl("torre-sticker");
	const torreSticker2Entry = page.canvasImages?.find(
		(img) => img.id === "torre-sticker-2",
	);
	const torreSticker2 = torreSticker2Entry?.notShow
		? undefined
		: torreSticker2Entry?.imageUrl;

	const introText = page.texts?.[1]?.content;
	const subtitle = page.texts?.[2]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const reserva =
		page?.reserva ?? page?.data?.reserva ?? page?.data?.data?.reserva;
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;
	const reservaOnSticker = sections.some((section) => section?.stickerCanvasId);

	// Lógica de color del header
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	let actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{subtitle && (
				<Box
					data-block-id="text-0"
					component="h1"
					sx={{
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: isSmallScreen ? "20px" : "24px",
						fontWeight: 700,
						textAlign: "left",
						margin: "12px 16px 0",
						color: contentColor,
					}}
				>
					{subtitle}
				</Box>
			)}

			<Box
				data-block-id="text-1"
				sx={{
					padding: "18px 12px 5px",
					position: "relative",
					marginTop: 0,
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				{introText && (
					<Box
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginTop: "-10px",
							paddingTop: "0px",
							padding: 0,
						}}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						marginTop: "0px",
						paddingTop: "0px",
						flex: 1,
						minHeight: 0,
						overflow: "visible",
					}}
				>
					{sections.map((section, idx) => {
						const imageSrc =
							section.layout === "box"
								? getCanvasImageUrl(section.imageId)
								: getImageSrc(section.imageId);
						if (section.layout === "box") {
							const isHarryPotterBox =
								section.imageId === "harry-potter-sticker";
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{
										margin: 0,
										marginTop: isSmallScreen && isHarryPotterBox ? "30px" : 0,
										padding: 0,
										overflow: "visible",
									}}
								>
									<Box
										sx={{
											display: "flex",
											gap: "16px",
											position: "relative",
											overflow: "visible",
										}}
									>
										<Box sx={{ width: "100%", overflow: "visible" }}>
											<Box
												component="p"
												sx={{
													...estiloP,
													padding: "12px",
													backgroundColor: accent.boxBg,
													borderRadius: "12px",
													position: "relative",
													overflow: "visible",
												}}
											>
												{imageSrc && (
													<img
														src={imageSrc}
														alt={section.title}
														style={
															isHarryPotterBox
																? {
																		width: isSmallScreen ? 105 : 115,
																		position: "absolute",
																		right: -34,
																		top: 8,
																		zIndex: 2,
																	}
																: {
																		width: isSmallScreen ? 105 : 115,
																		float: "right",
																		marginRight: -34,
																		marginTop: -40,
																	}
														}
													/>
												)}
												{section.title && (
													<Box component="h3" sx={estiloH3}>
														{section.title}
													</Box>
												)}
												<Box
													component="span"
													sx={{
														display: "block",
														width: isHarryPotterBox ? "80%" : "100%",
													}}
												>
													{section.body}
												</Box>
												{section.bubble && (
													<Box
														sx={{
															position: "absolute",
															right: 0,
															top: isSmallScreen ? -35 : -25,
															backgroundColor: "#fff8db",
															border: "2px solid #f5c400",
															borderRadius: "16px 16px 0px 16px",
															padding: "8px 10px",
															fontSize: 11,
															fontFamily: `${
																fontFamily || "'Poppins', sans-serif"
															}`,
															color: contentColor,
															boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
															width: isSmallScreen ? 150 : 200,
															zIndex: 6,
															lineHeight: 1.35,
														}}
													>
														{section.bubble}
													</Box>
												)}
											</Box>
										</Box>
									</Box>
									{reserva && !reservaOnSticker && (
										<>
											<Box
												sx={{
													position: "absolute",
													bottom: isSmallScreen ? 100 : 70,
													left: isSmallScreen ? 60 : 4,
													backgroundColor: accent.tooltipBg,
													border: "2px solid #f5c400",
													borderRadius: "16px 16px 16px 0px",
													padding: "10px 12px",
													fontSize: 11,
													fontFamily: `${
														fontFamily || "'Poppins', sans-serif"
													}`,
													color: contentColor,
													boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
													maxWidth: isSmallScreen ? 240 : 120,
													zIndex: 6,
												}}
											>
												<a
													href={reserva.url}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														color: "#000000",
														fontWeight: 700,
														textDecoration: "underline",
													}}
												>
													{reserva.text}
												</a>{" "}
												{reserva.suffix}
											</Box>
											{torreSticker2 && (
												<img
													src={torreSticker2}
													alt="Torre Londres"
													style={{
														width: 100,
														position: "absolute",
														bottom: isSmallScreen ? 70 : 0,
														left: -20,
														zIndex: 5,
													}}
												/>
											)}
										</>
									)}
								</Box>
							);
						}
						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 2}`}
								sx={{
									display: "flex",
									gap: "16px",
									alignItems: "flex-start",
									position: "relative",
									overflow: "visible",
									margin: 0,
									padding: 0,
								}}
							>
								{imageSrc && (
									<Box
										sx={{
											width: "40%",
											position: "relative",
											flexShrink: 0,
											overflow: "visible",
											lineHeight: 0,
											fontSize: 0,
										}}
									>
										<img
											src={imageSrc}
											alt={section.title}
											style={{
												width: "100%",
												height: 110,
												objectFit: "cover",
												borderRadius: "12px",
												display: "block",
											}}
										/>
										{section.stickerCanvasId &&
											!section.showReserva &&
											getCanvasImageUrl(section.stickerCanvasId) && (
												<>
													<img
														src={getCanvasImageUrl(section.stickerCanvasId)}
														alt=""
														style={{
															width: 95,
															position: "absolute",
															bottom:
																section.stickerCanvasId ===
																"grassmarket-sticker"
																	? -40
																	: isSmallScreen
																		? -80
																		: 0,
															left: -30,
															zIndex: 5,
														}}
													/>
													{section.bubble && (
														<Box
															sx={{
																position: "absolute",
																left: 20,
																bottom: isSmallScreen ? -30 : 72,
																backgroundColor: "#fff8db",
																border: "2px solid #f5c400",
																borderRadius: "16px 16px 16px 0px",
																padding: "8px 10px",
																fontSize: 11,
																fontFamily: `${
																	fontFamily || "'Poppins', sans-serif"
																}`,
																color: contentColor,
																boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
																width: isSmallScreen ? 130 : 150,
																zIndex: 6,
																lineHeight: 1.35,
															}}
														>
															{section.bubble}
														</Box>
													)}
												</>
											)}
									</Box>
								)}
								{reserva &&
									section.showReserva &&
									getCanvasImageUrl(section.stickerCanvasId) && (
										<>
											<img
												src={getCanvasImageUrl(section.stickerCanvasId)}
												alt=""
												style={{
													width: 88,
													position: "absolute",
													right: -30,
													bottom: -53,
													zIndex: 5,
												}}
											/>
											<Box
												sx={{
													position: "absolute",
													right: 50,
													bottom: -30,
													backgroundColor: "#fff8db",
													border: "2px solid #f5c400",
													borderRadius: "16px 16px 0px 16px",
													padding: "10px 12px",
													fontSize: 11,
													fontFamily: `${
														fontFamily || "'Poppins', sans-serif"
													}`,
													color: contentColor,
													boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
													width: isSmallScreen ? 170 : 200,
													zIndex: 6,
												}}
											>
												<a
													href={reserva.url}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														color: "#000000",
														fontWeight: 700,
														textDecoration: "underline",
													}}
												>
													{reserva.text}
												</a>{" "}
												{reserva.suffix}
											</Box>
										</>
									)}
								{section.sabiasQue &&
									section.sabiasQue.map((sq, i) => (
										<Box
											key={i}
											sx={{
												position: "absolute",
												top: i === 0 ? -10 : 30 + (i - 1) * 2,
												left: "calc(40% - 22px)",
											}}
										>
											<SabiasQue
												icono={sq.icono}
												texto={sq.texto}
												position={
													idx === sections.length - 1 ? "above" : undefined
												}
											/>
										</Box>
									))}
								<Box
									sx={{
										width: "60%",
										paddingRight: section.showReserva ? "120" : 0,
										boxSizing: "border-box",
									}}
								>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box
											component="p"
											sx={{ ...estiloP, fontSize: 13, color: contentColor }}
										>
											{section.body}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}

					{llegar && llegar.title && (
						<Box
							data-block-id="text-6"
							sx={{
								display: "flex",
								flex: 1,
								minHeight: 0,
								marginTop: isSmallScreen ? "90px" : "0",
							}}
						>
							<Box
								sx={{
									backgroundColor: accent.boxBg,
									border: `3px solid ${accent.boxBorder}`,
									borderRadius: "12px",
									padding: "8px 20px",
									fontSize: 12,
									fontFamily: `${
										fontFamily || "'Poppins', sans-serif"
									} !important`,
									color: "#333",
									marginBottom: isSmallScreen ? "12px" : 0,
									width: "100%",
									height: "100%",
									boxSizing: "border-box",
								}}
							>
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 6,
										color: accent.accent,
										textTransform: "uppercase",
									}}
								>
									{llegar.title}
								</Box>
								{llegar.content && (
									<Box
										component="div"
										dangerouslySetInnerHTML={{ __html: llegar.content }}
										sx={{
											paddingLeft: "0px",
											margin: 0,
											lineHeight: 1.5,
											"& ul": { paddingLeft: 4, margin: 0 },
										}}
									/>
								)}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template330.thumbnail = "/images/template-1.png";

export default Template330;
